import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";

const STATUS_STYLES = {
  pending: "bg-[#F4A261]/15 text-[#F4A261] border-[#F4A261]/30",
  approved: "bg-[#0D9488]/15 text-[#0D9488] border-[#0D9488]/30",
  dispatched: "bg-[#0D9488]/15 text-[#0D9488] border-[#0D9488]/30",
  failed: "bg-[#D90429]/10 text-[#D90429] border-[#D90429]/30",
  rejected: "bg-black/5 text-[#717680] border-black/10",
};

const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function VoucherAI() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [synced, setSynced] = useState([]);
  const [ingested, setIngested] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dupWarning, setDupWarning] = useState(null); // { previous, sha256, pendingFile }
  const [docType, setDocType] = useState("bank_statement");
  const [bridge, setBridge] = useState({ api_key: "", enabled: false });
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [bridgeScript, setBridgeScript] = useState("");
  const [showInline, setShowInline] = useState(false);
  const [legacy, setLegacy] = useState([]);
  const [opening, setOpening] = useState([]);
  const [yoy, setYoy] = useState([]);
  const [openingDate, setOpeningDate] = useState("2025-04-01");
  const coaFileRef = useRef(null);
  const openingFileRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [p, s, b, l, o, y, ing] = await Promise.all([
        api.get("/voucher-ai/pending"),
        api.get("/voucher-ai/synced"),
        api.get("/voucher-ai/bridge/settings"),
        api.get("/voucher-ai/legacy/vouchers"),
        api.get("/voucher-ai/legacy/opening-balances"),
        api.get("/voucher-ai/legacy/yoy?years=3"),
        api.get("/voucher-ai/ingested"),
      ]);
      setPending(p.data || []);
      setSynced(s.data || []);
      setBridge(b.data || { api_key: "", enabled: false });
      setLegacy(l.data || []);
      setOpening(o.data || []);
      setYoy(y.data?.years || []);
      setIngested(ing.data || []);
    } catch (e) { /* silent */ }
  };

  const importCOA = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/voucher-ai/legacy/import-coa", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`COA import · ${data.inserted} new, ${data.updated} updated`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { if (coaFileRef.current) coaFileRef.current.value = ""; }
  };

  const importOpening = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("as_of_date", openingDate);
      const { data } = await api.post("/voucher-ai/legacy/import-opening", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Opening balances · ${data.inserted} rows as of ${data.as_of_date}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { if (openingFileRef.current) openingFileRef.current.value = ""; }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await uploadFile(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadFile = async (f, { force = false } = {}) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", docType);
      if (force) fd.append("force", "true");
      const { data } = await api.post("/voucher-ai/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      toast.success(`Parsed ${data.created} transaction(s) · review in Pending tab`);
      setDupWarning(null);
      setTab("ingestion");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.error === "duplicate_file") {
        setDupWarning({ ...detail, pendingFile: f });
        toast.error("Duplicate file — this exact document was uploaded before");
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const forceReupload = async () => {
    if (!dupWarning?.pendingFile) return;
    await uploadFile(dupWarning.pendingFile, { force: true });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) await uploadFile(f);
  };

  const reparse = async (id) => {
    try { await api.post(`/voucher-ai/ingested/${id}/reparse`); toast.success("Re-parse queued"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Re-parse failed"); }
  };
  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this document + original file?")) return;
    try { await api.delete(`/voucher-ai/ingested/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };
  const downloadDoc = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/voucher-ai/ingested/${id}/download`, "_blank");

  const approve = async (id) => {
    try {
      await api.post(`/voucher-ai/${id}/approve`);
      toast.success("Approved · queued for dispatch to Busy");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approve failed"); }
  };

  const reject = async (id) => {
    try {
      await api.post(`/voucher-ai/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reject failed"); }
  };

  const regenKey = async () => {
    if (bridge.api_key && !window.confirm(
      "This will immediately invalidate the current key.\n\n" +
      "The desktop script on your PC will stop working until you paste the NEW key into it.\n\n" +
      "The new key is shown ONCE and never again. Continue?"
    )) return;
    try {
      const { data } = await api.post("/voucher-ai/bridge/regenerate-key");
      setBridge({ api_key: data.api_key, enabled: true });
      toast.success(data.warning || "New bridge API key generated — download the script now");
    } catch (err) { toast.error("Failed to generate key"); }
  };

  const downloadScript = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/voucher-ai/bridge/download`;
    window.open(url, "_blank");
  };

  const viewScriptInline = async () => {
    try {
      const { data } = await api.get("/voucher-ai/bridge/download", { responseType: "text" });
      setBridgeScript(typeof data === "string" ? data : JSON.stringify(data));
      setShowInline(true);
    } catch (err) { toast.error("Failed to load script"); }
  };

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(bridgeScript);
      toast.success("Script copied — paste into a new file named reyansh_busy_bridge.py on your PC");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Layout title="Voucher AI" subtitle="Bank & Purchase → AI → GST/TDS → Busy Bridge">
      <div className="space-y-6">
        {/* Uploader */}
        <div className="border border-black/10 bg-white p-5" data-testid="voucher-ai-uploader">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Upload bank statement or purchase bill</h2>
              <p className="text-xs text-[#717680] mt-1">PDF · Max 15 MB · Gemini AI extracts transactions, applies GST + TDS rules, and drops them into Pending Approvals.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type-select" className="border border-black/15 rounded-none px-3 py-2 text-sm">
                <option value="bank_statement">Bank statement</option>
                <option value="purchase_invoice">Purchase invoice</option>
              </select>
              <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls" onChange={handleUpload} className="hidden" data-testid="voucher-file-input"/>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">
                {uploading ? "Parsing…" : "Upload & parse"}
              </button>
              <button onClick={() => setBridgeOpen(true)} data-testid="bridge-settings-btn" className="px-4 py-2 border border-black/15 text-sm font-semibold hover:bg-black hover:text-white">
                🔗 Busy Bridge {bridge.enabled ? <span className="text-[10px] text-[#0D9488] font-mono-shelv ml-1">● LIVE</span> : <span className="text-[10px] text-[#717680] font-mono-shelv ml-1">OFF</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-black/10 flex-wrap">
          <button onClick={() => setTab("ingestion")} data-testid="tab-ingestion" className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "ingestion" ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680]"}`}>
            📥 Ingestion Dashboard ({ingested.length})
          </button>
          <button onClick={() => setTab("pending")} data-testid="tab-pending" className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "pending" ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680]"}`}>
            Pending Approvals ({pending.length})
          </button>
          <button onClick={() => setTab("synced")} data-testid="tab-synced" className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "synced" ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680]"}`}>
            Synced Vouchers ({synced.length})
          </button>
          <button onClick={() => setTab("legacy")} data-testid="tab-legacy" className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === "legacy" ? "border-[#D90429] text-[#D90429]" : "border-transparent text-[#717680]"}`}>
            🔒 Legacy Data ({legacy.length})
          </button>
        </div>

        {/* Tables */}
        {tab === "ingestion" && (
          <div className="space-y-4" data-testid="ingestion-section">
            {dupWarning && (
              <div className="border-l-4 border-[#D90429] bg-[#FFE5E9]/60 p-4 flex items-start gap-3" data-testid="duplicate-file-banner">
                <div className="text-2xl leading-none pt-0.5">⚠️</div>
                <div className="flex-1 text-sm">
                  <div className="font-display font-bold text-[#D90429] text-base">Duplicate file blocked</div>
                  <div className="mt-1 text-[#4A0212]">
                    This exact file was already uploaded on{" "}
                    <b>{(dupWarning.previous?.uploaded_at || "").replace("T", " ").slice(0, 16)}</b> as{" "}
                    <b>{dupWarning.previous?.filename}</b> ({dupWarning.previous?.doc_type?.replace("_", " ")}) —{" "}
                    {dupWarning.previous?.txns_extracted || 0} transactions already extracted. No new vouchers were created.
                  </div>
                  <div className="mt-1 text-[10px] font-mono-shelv text-[#717680] break-all">
                    SHA-256 · {dupWarning.sha256}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={forceReupload}
                      data-testid="duplicate-force-btn"
                      className="px-3 py-1.5 text-xs font-semibold border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white"
                    >
                      Upload anyway (create duplicate)
                    </button>
                    <button
                      onClick={() => setDupWarning(null)}
                      data-testid="duplicate-dismiss-btn"
                      className="px-3 py-1.5 text-xs font-semibold border border-black/15 hover:bg-black hover:text-white"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed p-10 text-center cursor-pointer transition-all ${dragOver ? "border-[#D90429] bg-[#FFE5E9]/40" : "border-black/20 bg-white hover:border-[#D90429]/40"}`}
              data-testid="drop-zone"
            >
              <div className="text-4xl mb-3">📥</div>
              <div className="font-display text-lg font-bold tracking-tight">Drag &amp; drop a PDF or Excel file here</div>
              <div className="text-xs text-[#717680] mt-1">…or click anywhere in this box to browse · Max 15 MB · Currently uploading as: <b>{docType.replace("_", " ")}</b> (switch above)</div>
              {uploading && <div className="mt-3 text-xs font-mono-shelv text-[#F4A261]">● UPLOADING & PARSING…</div>}
            </div>

            {ingested.length === 0 ? (
              <div className="text-center text-[#717680] py-8 border border-dashed border-black/10">No files uploaded yet.</div>
            ) : (
              <div className="border border-black/10 bg-white overflow-hidden" data-testid="ingestion-queue">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F6F8] text-left">
                    <tr>
                      <th className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">File</th>
                      <th className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">Type</th>
                      <th className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">Status</th>
                      <th className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">Txns</th>
                      <th className="px-3 py-2 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">Uploaded</th>
                      <th className="px-3 py-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingested.map((d) => {
                      const badge = d.status === "ready_for_review" ? "bg-[#0D9488]/15 text-[#0D9488] border-[#0D9488]/30"
                        : d.status === "ai_processing" || d.status === "uploading" ? "bg-[#F4A261]/15 text-[#F4A261] border-[#F4A261]/30 animate-pulse"
                        : "bg-[#D90429]/10 text-[#D90429] border-[#D90429]/30";
                      const label = { uploading: "Uploading", ai_processing: "AI Processing", ready_for_review: "Ready for Review", failed: "Failed" }[d.status] || d.status;
                      return (
                        <tr key={d.id} className="border-t border-black/5" data-testid={`ingestion-row-${d.id}`}>
                          <td className="px-3 py-2 max-w-xs truncate" title={d.filename}>{d.filename}</td>
                          <td className="px-3 py-2 text-[#717680]">{d.doc_type.replace("_"," ")}</td>
                          <td className="px-3 py-2"><span className={`text-[10px] font-mono-shelv uppercase tracking-widest border px-1.5 py-0.5 ${badge}`} data-testid={`ingestion-status-${d.id}`}>{label}</span></td>
                          <td className="px-3 py-2 font-mono text-xs">{d.txns_extracted || 0}</td>
                          <td className="px-3 py-2 text-[10px] text-[#717680] font-mono-shelv">{(d.uploaded_at || "").slice(0, 19).replace("T"," ")}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button onClick={() => downloadDoc(d.id)} data-testid={`download-doc-${d.id}`} className="px-2 py-1 border border-black/15 text-[10px] hover:bg-black hover:text-white ml-1">⬇</button>
                            <button onClick={() => reparse(d.id)} data-testid={`reparse-doc-${d.id}`} disabled={d.status === "ai_processing"} className="px-2 py-1 border border-black/15 text-[10px] hover:bg-black hover:text-white ml-1 disabled:opacity-40">↻</button>
                            <button onClick={() => deleteDoc(d.id)} data-testid={`delete-doc-${d.id}`} className="px-2 py-1 border border-[#D90429]/40 text-[#D90429] text-[10px] hover:bg-[#D90429] hover:text-white ml-1">✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {ingested.some((d) => d.error) && (
              <div className="border border-[#D90429]/30 bg-[#FFE5E9]/40 p-3 text-[11px] text-[#D90429] font-mono-shelv">
                Some files failed. Hover the ↻ button to re-parse, or use browser DevTools → Network to see the error.
              </div>
            )}
          </div>
        )}

        {tab === "pending" && (
          <div className="space-y-3" data-testid="pending-list">
            {pending.length === 0 && <div className="text-center text-[#717680] py-12">No pending vouchers. Upload a bank statement or purchase bill to start.</div>}
            {pending.map((v) => <VoucherCard key={v.id} v={v} onApprove={() => approve(v.id)} onReject={() => reject(v.id)} showActions={true} />)}
          </div>
        )}

        {tab === "synced" && (
          <div className="space-y-3" data-testid="synced-list">
            {synced.length === 0 && <div className="text-center text-[#717680] py-12">No synced vouchers yet.</div>}
            {synced.map((v) => <VoucherCard key={v.id} v={v} showActions={false} />)}
          </div>
        )}

        {tab === "legacy" && (
          <div className="space-y-6" data-testid="legacy-section">
            <div className="border border-[#F4A261]/40 bg-[#F4A261]/5 p-4">
              <div className="font-mono-shelv text-[10px] uppercase tracking-widest text-[#F4A261] font-bold">★ Legacy Data — READ-ONLY guardrail active</div>
              <div className="mt-2 text-[12px] text-[#4A4D57]">Historical vouchers (dated before 2025-04-01) or flagged as legacy imports are locked. AI cannot edit, approve, or reject them — they are for reference and reporting only.</div>
            </div>

            {/* Year-over-Year */}
            {yoy.length > 0 && (
              <div className="border border-black/10 bg-white p-5" data-testid="yoy-panel">
                <h3 className="font-display text-lg font-bold tracking-tight">Year-over-Year</h3>
                <table className="w-full mt-3 text-sm">
                  <thead className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680] border-b border-black/10">
                    <tr><th className="text-left py-2">FY</th><th className="text-right py-2">Income</th><th className="text-right py-2">Expense</th><th className="text-right py-2">Profit</th></tr>
                  </thead>
                  <tbody>
                    {yoy.map((y) => (
                      <tr key={y.fy} className="border-b border-black/5" data-testid={`yoy-row-${y.fy}`}>
                        <td className="py-2 font-bold">{y.fy}</td>
                        <td className="py-2 text-right font-mono">₹{fmt(y.income)}</td>
                        <td className="py-2 text-right font-mono">₹{fmt(y.expense)}</td>
                        <td className={`py-2 text-right font-mono font-bold ${y.profit >= 0 ? "text-[#0D9488]" : "text-[#D90429]"}`}>₹{fmt(y.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* CSV importers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-black/10 bg-white p-5">
                <h3 className="font-display text-lg font-bold tracking-tight">Import Chart of Accounts</h3>
                <p className="text-xs text-[#717680] mt-1">CSV columns: <code>code, name, type, group</code>. Existing ledgers with the same name are updated in place — no duplicates.</p>
                <input ref={coaFileRef} type="file" accept=".csv" onChange={importCOA} className="hidden" data-testid="coa-file-input"/>
                <button onClick={() => coaFileRef.current?.click()} data-testid="import-coa-btn" className="mt-3 px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">⬆ Import COA CSV</button>
              </div>
              <div className="border border-black/10 bg-white p-5">
                <h3 className="font-display text-lg font-bold tracking-tight">Import Opening Balances</h3>
                <p className="text-xs text-[#717680] mt-1">CSV columns: <code>ledger, debit, credit</code>. As-of date defaults to 1-Apr FY start.</p>
                <div className="mt-3 flex items-center gap-2">
                  <input type="date" value={openingDate} onChange={(e) => setOpeningDate(e.target.value)} className="border border-black/15 rounded-none px-3 py-2 text-sm" data-testid="opening-as-of-date"/>
                  <input ref={openingFileRef} type="file" accept=".csv" onChange={importOpening} className="hidden" data-testid="opening-file-input"/>
                  <button onClick={() => openingFileRef.current?.click()} data-testid="import-opening-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">⬆ Import CSV</button>
                </div>
                {opening.length > 0 && <div className="mt-3 text-[10px] font-mono-shelv text-[#717680]">{opening.length} opening-balance rows loaded</div>}
              </div>
            </div>

            {/* Historical vouchers (read-only) */}
            {legacy.length === 0 ? (
              <div className="text-center text-[#717680] py-8 border border-dashed border-black/10">No legacy vouchers yet. Historical transactions from previous FYs will appear here once the bridge pushes them.</div>
            ) : (
              <div className="space-y-3" data-testid="legacy-list">
                {legacy.slice(0, 50).map((v) => <VoucherCard key={v.id} v={v} showActions={false} />)}
                {legacy.length > 50 && <div className="text-center text-[10px] font-mono-shelv text-[#717680]">Showing 50 of {legacy.length}</div>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bridge Settings Modal */}
      {bridgeOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBridgeOpen(false)}>
          <div className="bg-white border border-black/15 max-w-2xl w-full p-6 rounded-none" onClick={(e) => e.stopPropagation()} data-testid="bridge-modal">
            <h3 className="font-display text-xl font-bold tracking-tight">Busy Desktop Bridge Setup</h3>
            <div className="mt-4 border border-[#0D9488]/30 bg-[#0D9488]/5 p-3 text-[12px] leading-relaxed">
              <b>How it works:</b> Because Busy runs locally on your PC (not reachable from the cloud), a small Python script polls Reyansh CRM every 30 s and pushes approved vouchers into Busy's HTTP API (<code>http://localhost:8080</code>).
              <ol className="mt-2 space-y-1 list-decimal list-inside">
                <li>Generate an API key below (unique to your workspace)</li>
                <li>Either <b>Download</b> the script (saves to your browser's Downloads folder) OR <b>View inline</b> to copy-paste the code into a file at any location on your PC</li>
                <li>On your PC open a terminal, <b>cd</b> to the folder that contains the file, then run: <code>pip install requests</code> once, then <code>python reyansh_busy_bridge.py</code></li>
                <li>Leave it running. Approved vouchers land in Busy within 30 s.</li>
              </ol>
              <div className="mt-2 text-[11px] text-[#D90429]"><b>Troubleshooting</b> "No such file": your terminal isn't in the folder that has the script. Run <code>cd Downloads</code> first (Windows default) or <code>cd ~/Downloads</code> (Mac/Linux).</div>
            </div>
            <div className="mt-3 border border-[#D90429]/30 bg-[#FFE5E9]/40 p-3 text-[11px] leading-relaxed" data-testid="bridge-security-notice">
              <b>🔒 Security</b> · The key is sent to the server in the <code>X-API-Key</code> request header only. Query-string auth (<code>?api_key=…</code>) is refused with HTTP 400 — URLs end up in web-server access logs. Regenerating a key <b>immediately kills the old one</b> and the new value is shown exactly once. Never commit this key to GitHub or paste it into chat.
            </div>
            <div className="mt-4">
              <label className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680]">API Key</label>
              <div className="flex items-center gap-2 mt-1">
                <input value={bridge.api_key || ""} readOnly placeholder="No key yet — click Generate" className="flex-1 border border-black/15 px-3 py-2 text-xs font-mono bg-black/5 rounded-none" data-testid="bridge-api-key"/>
                <button onClick={() => { navigator.clipboard.writeText(bridge.api_key || ""); toast.success("Copied"); }} disabled={!bridge.api_key} className="px-3 py-2 border border-black/15 text-xs disabled:opacity-40">Copy</button>
              </div>
            </div>

            {showInline && (
              <div className="mt-4" data-testid="bridge-script-inline">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-mono-shelv uppercase tracking-widest text-[#717680]">reyansh_busy_bridge.py — copy this into a file on your PC</label>
                  <button onClick={copyScript} data-testid="copy-script-btn" className="px-3 py-1 border border-black/15 text-xs hover:bg-black hover:text-white">Copy script</button>
                </div>
                <textarea value={bridgeScript} readOnly className="w-full border border-black/15 px-3 py-2 text-[10px] font-mono bg-black/5 rounded-none h-64" data-testid="bridge-script-textarea"/>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
              <button onClick={regenKey} data-testid="regen-key-btn" className="px-4 py-2 border border-black/15 text-sm hover:bg-black hover:text-white">
                {bridge.api_key ? "Regenerate key" : "Generate API key"}
              </button>
              <div className="flex flex-wrap gap-2">
                <button onClick={viewScriptInline} disabled={!bridge.api_key} data-testid="view-inline-btn" className="px-4 py-2 border border-black/15 text-sm disabled:opacity-40">
                  👁 View inline
                </button>
                <button onClick={downloadScript} disabled={!bridge.api_key} data-testid="download-bridge-btn" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40">
                  ⬇ Download script
                </button>
                <button onClick={() => setBridgeOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function VoucherCard({ v, onApprove, onReject, showActions }) {
  const totalDr = (v.lines || []).reduce((s, ln) => s + (Number(ln.debit) || 0), 0);
  const totalCr = (v.lines || []).reduce((s, ln) => s + (Number(ln.credit) || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.5;
  return (
    <div className="border border-black/10 bg-white p-4" data-testid={`voucher-card-${v.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest font-mono-shelv text-[#717680]">{v.txn_date}</span>
            <span className="font-bold">{v.vendor || "—"}</span>
            {v.vendor_gstin && <span className="text-[10px] font-mono-shelv text-[#717680]">GSTIN {v.vendor_gstin}</span>}
            <span className={`text-[10px] font-mono-shelv uppercase tracking-widest border px-1.5 py-0.5 ${STATUS_STYLES[v.status] || ""}`}>{v.status}</span>
            {v.is_legacy && <span className="text-[10px] font-mono-shelv uppercase tracking-widest bg-[#F4A261]/15 text-[#F4A261] border border-[#F4A261]/30 px-1.5 py-0.5" data-testid={`legacy-badge-${v.id}`}>🔒 Legacy · {v.legacy_source || "read-only"}</span>}
            {v.gst_split?.mode && <span className="text-[10px] font-mono-shelv uppercase tracking-widest bg-[#0D9488]/10 text-[#0D9488] border border-[#0D9488]/30 px-1.5 py-0.5">{v.gst_split.mode}</span>}
            {v.tds_split?.section && <span className="text-[10px] font-mono-shelv uppercase tracking-widest bg-[#F4A261]/10 text-[#F4A261] border border-[#F4A261]/30 px-1.5 py-0.5" data-testid={`tds-badge-${v.id}`}>TDS {v.tds_split.section} · ₹{fmt(v.tds_split.amount)}</span>}
          </div>
          <div className="mt-1 text-sm text-[#4A4D57]">{v.narration || v.reference}</div>
          <div className="text-[10px] text-[#717680] font-mono-shelv mt-0.5">Source: {v.source_type} · {v.source_file || "manual"}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold">₹{fmt(v.amount)}</div>
          {!balanced && <div className="text-[10px] text-[#D90429] font-mono-shelv">⚠ Dr ≠ Cr</div>}
        </div>
      </div>

      <table className="w-full mt-3 text-xs">
        <thead>
          <tr className="text-[#717680] font-mono-shelv uppercase text-[10px] tracking-widest border-b border-black/5">
            <th className="text-left py-1.5">Ledger</th>
            <th className="text-right py-1.5">Debit</th>
            <th className="text-right py-1.5">Credit</th>
            <th className="text-left py-1.5 pl-2">Narration</th>
          </tr>
        </thead>
        <tbody>
          {(v.lines || []).map((ln, idx) => (
            <tr key={idx} className="border-b border-black/5" data-testid={`voucher-line-${v.id}-${idx}`}>
              <td className="py-1.5">{ln.ledger}</td>
              <td className="text-right font-mono">{ln.debit ? `₹${fmt(ln.debit)}` : "—"}</td>
              <td className="text-right font-mono">{ln.credit ? `₹${fmt(ln.credit)}` : "—"}</td>
              <td className="pl-2 text-[#717680]">{ln.narration}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {v.dispatch_status && (
        <div className="mt-2 text-[10px] font-mono-shelv text-[#717680]">Bridge → {v.dispatch_status}</div>
      )}

      {showActions && (
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={onReject} data-testid={`reject-btn-${v.id}`} className="px-3 py-1.5 border border-black/15 text-xs hover:bg-black hover:text-white">Reject</button>
          <button onClick={onApprove} disabled={!balanced} data-testid={`approve-btn-${v.id}`} className="px-3 py-1.5 bg-[#D90429] text-white text-xs font-semibold hover:bg-[#EF233C] disabled:opacity-40">✓ Approve &amp; queue for Busy</button>
        </div>
      )}
    </div>
  );
}
